import {
    DataSourceRowsChange,
    ICubeSourceDataSource,
    ICubeSourceWithDataSources,
    IDataRow,
    IDataSourceDimensionMetaData,
    IDataSourceFieldMetaData,
    IDataSourceOnRowsChangedEvent,
    IKnownKeyDimensionOptions,
    IKnownKeyOptions,
    ILoadDimensionOptions,
    ILoadOptions,
    ILoadStatus,
    IRowScope
} from "./CubeSource.IF";
import {WB_CubeSourceDataSourceRow} from "../PA.Core/PAdbSchemaRows";
import {IProgressProvider} from "../PA.Core/PA.Core.IF.Basic";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {DimensionType} from "../PA.Core/PA.Core.IF.Hierarchy";
import {
    ICellMetric,
    IDataLocation,
    IDimensionCompositeKeys,
    IDimensionDistinctKeys,
    IKeyOptions
} from "./PA.HyperRectangle.IF";
import * as _ from "lodash"
import {ProgressProvider} from "../PA.Core/Basic/Progress";
import {ESRecordMapper} from "../PA.Core/lib/ESRecordMapper";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import { CreateOptions, ESRecordResolver } from "../PA.Core/lib/ESRecordResolver";
import {IContinueConditionState, WaitForGroup} from "../PA.Core/Basic/WaitForGroup";
import {BehaviorSubject} from "rxjs";
import {JSONUtils} from "../PA.Core/Basic/JSON";
import {ExecutionSerializer} from "../PA.Core/Basic/ExecutionSerializer";
import { DataSourceColumnMetric } from "./DataSourceColumnMetric";
import { CubeSourceMetaDataService } from "../PA.HyperRectangle.Services/CubeSourceMetaDataService";


const LoadDimensionOptions_default: ILoadDimensionOptions = {
    bEnableLazyLoading: false,
    InitialLeafKeys: []
}

const KnownKeyOptions_default: IKnownKeyDimensionOptions = {
    bUseKnownKeys: false,
    KnownLeafKeys: []
}

export interface IDimensionPropertyCreateOptions {
    [dimType: string]: {
        LeafLevel: string
    }
}

export interface IDataSourceJSDataCreateOptions {
    dbContext: ESDataStore,
    LoadOptions: ILoadOptions,
    KnownKeyOptions?: IKnownKeyOptions,
    DimensionProperties: IDimensionPropertyCreateOptions,
    RecordResolverOptions?: CreateOptions
}

interface IDimensionProperties {
    //-- Row Properties
    KeyFieldName: string
    bIsCubeSourceParameter: boolean,
    ParameterName: string,

    //-- Hierarchy Properties
    bHierarchyEnabled: boolean,
    LeafLevel: string


    //-- Known Keys
    bUseKnownKeys: boolean
    KnownLeafKeys: (number | string)[],
    KnownLeafKeysIdx: {[id: number | string]: number | string}

    //-- Loading Properties
    bLazyLoadingEnabled: boolean
    dctLeafIdQueuedById: { [id: number | string]: number | string }
    dctLeafIdLoadedById: { [id: number | string]: number | string }
    bLoadAllQueued: boolean
    bLoadAllLoaded: boolean

}

const DimensionProperties_default: IDimensionProperties = {
    //-- Row Properties
    KeyFieldName: "",
    bIsCubeSourceParameter: false,
    ParameterName: "",

    //-- Hierarchy Properties
    bHierarchyEnabled: true,
    LeafLevel: "",

    //-- Known Keys
    bUseKnownKeys: false,
    KnownLeafKeys: [],
    KnownLeafKeysIdx: {},

    //-- Loading Properties
    bLazyLoadingEnabled: false,
    dctLeafIdQueuedById: {},
    dctLeafIdLoadedById: {},
    bLoadAllQueued: false,
    bLoadAllLoaded: false
}

export class DataSourceJSData
 implements
     ICubeSourceDataSource
{
    private _bIsLoaded: boolean
    private _OnLoadComplete: Promise<void>
    private _CubeSource: ICubeSourceWithDataSources
    private _CubeSourceDataSourceRow: WB_CubeSourceDataSourceRow
    private _LoadProgressProvider: ProgressProvider

    private dbContext: ESDataStore
    private recordMapper: ESRecordMapper
    private recordResolverOptions: CreateOptions
    private pkFieldName: string

    private fieldProps: {[columnId: string]: IDataSourceFieldMetaData}

    private loadOptions: ILoadOptions
    private knownKeyOptions: IKnownKeyOptions
    private dimensionProps: {[dimType: string]: IDimensionProperties }
    private dimensionPropCreateOptions: IDimensionPropertyCreateOptions

    // Loading
    private bQueueLoadProcessRunning: boolean
    private bQueueLoadProcessPaused: boolean
    private bResetDataQueued: boolean

    private waitForDataGroup: WaitForGroup<DataSourceJSData>
    private dctLoadedRows: {[id: string | number]: ESRecord}
    private qsProcessLoadQueue: ExecutionSerializer

    private _OnRowsChanged: BehaviorSubject<IDataSourceOnRowsChangedEvent>
    private _OnLoadProcessingComplete: BehaviorSubject<ICubeSourceDataSource>



    constructor(cubeSource: ICubeSourceWithDataSources,
                cubeSourceDataSourceRow: WB_CubeSourceDataSourceRow,
                options: IDataSourceJSDataCreateOptions)
    {

        this._bIsLoaded = false
        this._OnLoadComplete = undefined
        this._CubeSource = cubeSource
        this._CubeSourceDataSourceRow = cubeSourceDataSourceRow;
        this._LoadProgressProvider = new ProgressProvider()
        this.fieldProps = {}
        this.dimensionProps = {}
        this.dbContext = options.dbContext
        let mapperName = this._CubeSourceDataSourceRow.WB_DataObjectRow.cif.MAPPER_ID
        this.recordMapper = <any> this.dbContext.getMapper(mapperName)
        this.recordResolverOptions = options.RecordResolverOptions || {}
        this.pkFieldName = this.recordMapper.idAttribute

        this.loadOptions = options.LoadOptions || {}
        this.knownKeyOptions = options?.KnownKeyOptions || {}
        this.dimensionPropCreateOptions = options?.DimensionProperties || {}

        this.bQueueLoadProcessRunning = false
        this.bQueueLoadProcessPaused = false
        this.bResetDataQueued = false
        this.dctLoadedRows = {}
        this.waitForDataGroup = new WaitForGroup(this)
        this._OnRowsChanged = new BehaviorSubject<IDataSourceOnRowsChangedEvent>(undefined)
        this._OnLoadProcessingComplete = new BehaviorSubject<ICubeSourceDataSource>(undefined)

        this.qsProcessLoadQueue = new ExecutionSerializer(this, this._DataSourceJSData_processQueue, [],500)

        this.setLoadOptions(this.loadOptions)
    }

    //-- Load Options
    setLoadOptions(options: ILoadOptions): void {
        const self = this

        if(self.bIsLoaded)return
        if(!_.isUndefined(self._OnLoadComplete))return

        _.forEach(options, (aLoadOpts, dimType) => {
            if(!(dimType in self.dimensionProps))
                return

            let dp = self.dimensionProps[dimType]
            if(!dp.bHierarchyEnabled)return

            if(_.isBoolean(aLoadOpts.bEnableLazyLoading))
                dp.bLazyLoadingEnabled = aLoadOpts.bEnableLazyLoading

        })
    }


    get bIsLoaded(): boolean { return this._bIsLoaded }
    get OnLoadComplete(): Promise<void> {
        const self = this
        if(self.bIsLoaded)
            return Promise.resolve()

        if(!_.isUndefined(self._OnLoadComplete))
            return self._OnLoadComplete

        let returnValue = self._OnLoadComplete = (async ()=> {
            //--
            //--  Setup Dimension Properties

            // Setup Key Fields
            for(let aRow of self._CubeSourceDataSourceRow.WB_CubeSourceDataSourceDimensionRows){
                if(!aRow.isActive)
                    return
                let dimType = aRow.cif.DIMENSION_TYPE
                let keyFieldName = aRow.cif.KEY_FIELDNAME
                let loadOps = _.cloneDeep(_.defaults({}, self.loadOptions[dimType], LoadDimensionOptions_default))
                let keyOps = _.cloneDeep(_.defaults({}, self.knownKeyOptions[dimType], KnownKeyOptions_default))
                let dimCreateOps = self.dimensionPropCreateOptions[dimType] || {LeafLevel: ""}


                let dp = self.getOrCreateDimProperties(dimType)

                dp.KeyFieldName = keyFieldName
                dp.LeafLevel = dimCreateOps?.LeafLevel
                dp.bLazyLoadingEnabled = loadOps.bEnableLazyLoading
                dp.bUseKnownKeys = keyOps.bUseKnownKeys
                dp.KnownLeafKeys = keyOps.KnownLeafKeys
                dp.KnownLeafKeysIdx = _.keyBy(dp.KnownLeafKeys, (a) => a)

                if(_.isEmpty(self._CubeSource.getDimensionHierarchy(<any>dimType)))
                    dp.bHierarchyEnabled = false

                let csDSRow = self._CubeSourceDataSourceRow?.cif
                let csRow = self._CubeSourceDataSourceRow?.WB_CubeSourceRow?.cif
                if(csDSRow?.PARAMETER1_FIELDNAME == dp.KeyFieldName){
                    dp.bIsCubeSourceParameter = true
                    dp.ParameterName = csRow.PARAMETER1_NAME
                } else if(csDSRow?.PARAMETER2_FIELDNAME == dp.KeyFieldName){
                    dp.bIsCubeSourceParameter = true
                    dp.ParameterName = csRow.PARAMETER2_NAME
                }
            } // Next Dimension Row

            // Get Policy Metrics


            self._bIsLoaded = true
        })();

        return returnValue
    }

    private getOrCreateDimProperties(dimType: string): IDimensionProperties {
        if(!(dimType in this.dimensionProps)){
            this.dimensionProps[dimType] = _.cloneDeep(DimensionProperties_default)
        }

        return this.dimensionProps[dimType]
    }

    get DataSourceName(): string {
        let dsr = this._CubeSourceDataSourceRow
        let ds = dsr.cif
        return `${ds.ALIAS_ID || "Alias"}-${dsr.WB_DataObjectRow?.cif.DATAOBJECT_NAME}`
    }
    get Alias(): string { return this._CubeSourceDataSourceRow.cif.ALIAS_ID }

    get CubeSource(): ICubeSourceWithDataSources { return this._CubeSource }
    get CubeSourceDataSourceRow(): WB_CubeSourceDataSourceRow { return this._CubeSourceDataSourceRow  }
    get LoadProgressProvider(): IProgressProvider { return this._LoadProgressProvider }

    //-- Metric Functions
    async getColumnMetrics(options?: {bSkipColumnMetrics?: boolean, bSkipPolicyMetrics?: boolean}): Promise<ICellMetric[]> {
        const self = this
        const rv: ICellMetric[] = []

        if(!(options?.bSkipColumnMetrics)) {
            // Create Metrics for DataSource
            let metricColumns = _.filter(self.CubeSourceDataSourceRow.WB_DataObjectRow.WB_DataObjectColumnRows, (colRow) => {
                return (colRow.isActive && (colRow.cif.COLUMN_METATYPE == 'metric') && (colRow.cif.CUSTOM_IND != '1') )
            })
            for(let colRow of metricColumns) {
                const metric = new DataSourceColumnMetric(self.CubeSourceDataSourceRow, colRow)
                rv.push(metric)
            }
        }

        if((!(options?.bSkipPolicyMetrics)) && !_.isEmpty(self.CubeSourceDataSourceRow.cif.METRIC_POLICY)) {
            const csmds = new CubeSourceMetaDataService()
            const policyId = self.CubeSourceDataSourceRow.cif.METRIC_POLICY
            const dctPolicies = csmds.getCubeSourceMetricPolicies()
            const policy = dctPolicies[policyId]
            if(_.isFunction(policy?.getMetrics)){
                const pMtrcs = await policy.getMetrics(self, options)
                _.forEach(pMtrcs, (a) => {
                    rv.push(a)
                })
            }

        }

        return rv
    }

    //-- Row Metadata Functions
    getRowDimensionKeyField(dimensionType: DimensionType): string {
        return this.dimensionProps[dimensionType]?.KeyFieldName
    }

    getRowMetricFieldIds(): string[] { return _.keys(this.fieldProps); }

    getRowMetricFieldMetaData(aFieldId: string): IDataSourceFieldMetaData {
        if(aFieldId in this.fieldProps)
            return this.fieldProps[aFieldId]
        return {FieldId: aFieldId, FieldName: aFieldId, FieldDescription: "",
                FieldType: "na", FieldSort: 999, MetricId: ""}
    }

    getDimensionMetaData(dimensionType: DimensionType): IDataSourceDimensionMetaData {
        if(dimensionType in this.dimensionProps){
            return {DimensionType: dimensionType, LeafLevel: this.dimensionProps[dimensionType].LeafLevel}
        }

        return {DimensionType: dimensionType, LeafLevel: `Unknown-${dimensionType}`};
    }


    //-- Row Functions
    get LoadedRows(): IDataRow[] { return _.values(this.dctLoadedRows) }


    add(rows: ESRecord[] | ESRecord): void {
        // Add records into dictionary, and fire event
        // Adding deleted rows, removes them

        if(!_.isArray(rows)){
            rows = [rows]
        }
        _.forEach(rows, (aRow) => {
            // @ts-ignore
            let pk = aRow[this.pkFieldName]

            if(!aRow.isActive){
                if(pk in this.dctLoadedRows)
                    delete this.dctLoadedRows[pk]
                return
            }

            this.dctLoadedRows[pk] = aRow
        })

        this.OnRowsChanged.next({
            DataSource: this,
            Change: DataSourceRowsChange.Added,
            Rows: rows
        })
    }


    remove(rows: ESRecord[] | ESRecord | number[] | number): void {
        // Add records into dictionary, and fire event
        if (_.isNumber(rows)) {
            rows = [rows]
        }
        if(!_.isArray(rows)){
            rows = [rows]
        }

        _.forEach(rows, (aRow: any) => {
            if (_.isNumber(aRow)) {
                if (aRow in this.dctLoadedRows)
                    delete this.dctLoadedRows[aRow]
            } else {
                let pk = aRow[this.pkFieldName]
                if (pk in this.dctLoadedRows)
                    delete this.dctLoadedRows[pk]
            }
        })

        this.OnRowsChanged.next({
            DataSource: this,
            Change: DataSourceRowsChange.Removed,
            Rows: rows
        })

    }

    getLoadStatus(dimensionTypes: DimensionType[]): ILoadStatus {
        let rv: ILoadStatus = {}
        let keys = dimensionTypes
        if (_.isEmpty(keys)) { // @ts-ignore
            keys = _.keys(this.dimensionProps)
        }

        _.forEach(keys, (a) => {
            if(a in this.dimensionProps){
                let dp = this.dimensionProps[a]
                rv[a] = {
                    bLazyLoadingEnabled: dp.bLazyLoadingEnabled,
                    bLoadAllLoaded: dp.bLoadAllLoaded,
                    dctLeafIdLoadedById: _.clone(dp.dctLeafIdLoadedById),
                    bLoadAllQueued: dp.bLoadAllQueued,
                    dctLeafIdQueuedById: _.clone(dp.dctLeafIdQueuedById)
                }
            }
        })

        return rv
    }

    setLoadedKeys(dimensionType: DimensionType, keys: string | number | (string|number)[]): void {
        if(!(dimensionType in this.dimensionProps))
            return
        let items: (number|string)[] = []
        if(_.isArray(keys))
            items = keys
        else
            items = [keys]

        let dp = this.dimensionProps[dimensionType]
        _.forEach(items, (aKey) => {
            if(!(aKey in dp.dctLeafIdLoadedById))
                dp.dctLeafIdLoadedById[aKey] = aKey
        })

    }


    setKnownKeyOptions(options: IKnownKeyOptions): void {
        _.forEach(options, (kkOpts, key) => {
            if(key in this.dimensionProps){
                let dp = this.dimensionProps[key]
                if('bUseKnownKeys' in kkOpts)
                   dp.bUseKnownKeys = kkOpts.bUseKnownKeys
                if('KnownLeafKeys' in kkOpts) {
                    dp.KnownLeafKeys = kkOpts.KnownLeafKeys
                    dp.KnownLeafKeysIdx = _.keyBy(dp.KnownLeafKeys, (a) => a)
                }
            }
        })

        this._CubeSource.OnCubeSourcePropertyChange.next({
            Source: this._CubeSource,
            DataSource: this,
            PropertyName: "DataSource.KnownKeyOptions",
            PropertyValue: options
        })
    }


    async getDistinctKeys(dimensionTypes: DimensionType[], options: IKeyOptions): Promise<IDimensionDistinctKeys> {
        let returnValue: IDimensionDistinctKeys = {}
        for(let aDimType of dimensionTypes) {
            returnValue[aDimType] = []
            let dp = this.dimensionProps[aDimType]
            if(_.isEmpty(dp))
                continue

            switch(options.dataLocation){
                case IDataLocation.RemoteAvailable:
                    if(dp.bUseKnownKeys){
                        returnValue[aDimType].push.apply(returnValue[aDimType], dp.KnownLeafKeys)
                        break
                    } else {
                        //TODO:  Get distinct values from Remote, use PASearch Service
                        //  union with local Keys?
                    }
                    break;

                case IDataLocation.LocalLoaded:
                    if(dp.bLazyLoadingEnabled){
                        returnValue[aDimType].push.apply(returnValue[aDimType], _.values(dp.dctLeafIdLoadedById))
                        // Use the Queue to
                        if(options.bIncludedLoadQueue)
                            returnValue[aDimType].push.apply(returnValue[aDimType], _.values(dp.dctLeafIdQueuedById))
                        break;
                    } else {

                        let dctDistinctKeys: {[id: number | string]: number | string} = {}
                        _.forEach(this.LoadedRows, (aRow) => {
                            let keyVal = aRow[dp.KeyFieldName]
                            dctDistinctKeys[keyVal] = keyVal
                        })

                        returnValue[aDimType].push.apply(returnValue[aDimType], _.values(dctDistinctKeys))
                    }
                    break;
            }

        } // Next Dimension

        return returnValue;
    }

    async getCompositeKeys(dimensionTypes: DimensionType[], options: IKeyOptions): Promise<IDimensionCompositeKeys> {
        // TODO, not implemented yet.. may change API to include filtering
        return undefined;
    }

    //-- Loading Functions
    checkLoadScopeSync(scope: IRowScope): boolean {
        const self = this
        let returnValue = true
        let bScopeHasFilterAll = false

        _.forEach(this.dimensionProps, (dp, dimType) => {
            if(!dp.bHierarchyEnabled)return

            // If in scope, check coverage, else all must be loaded
            if(dimType in scope){

                // If the Dimension is fully loaded, then return true
                if(dp.bLoadAllLoaded)
                    return

                let rq = scope[dimType]
                if (rq.bAll) {
                    returnValue = false
                    return false // stop iterating
                }

                //-- If Not Lazy Loading, we know it's  not loaded
                if(!dp.bLazyLoadingEnabled){
                    returnValue = false
                    return false // stop iterating
                }

                let hier = rq.Hierarchy
                if (_.isEmpty(hier)) {
                    // @ts-ignore
                    hier = self.CubeSource.getDimensionHierarchy(dimType)
                }

                if (_.isEmpty(hier)){
                    let msg = `checkLoadScopeSync: Unknown Hierarchy ${dimType}`
                    console.log(msg)
                    return false
                }

                if (_.isEmpty(rq.HierarchyNodes))
                    bScopeHasFilterAll = true

                // Find All Leaf Nodes and Check if
                let leafNodes1 = _.filter(rq.HierarchyNodes, (a) => (a?.NodeLevel == dp.LeafLevel))
                let leafNodes2 = hier.getChildren(rq.HierarchyNodes, dp.LeafLevel)
                _.forEach([...leafNodes1, ...leafNodes2], (aLeaf) => {
                    if (dp.bUseKnownKeys) {
                        if (!(aLeaf.NodeId in dp.KnownLeafKeysIdx))
                            return //-- Check next Key, not Known Key
                    }

                    if (!(aLeaf.NodeId in dp.dctLeafIdLoadedById)) {
                        //-- Found missing key
                        returnValue = false
                    }
                })

            } else {  //-- Dimension not in Scope, Assumes All
                //-- All must be loaded
                if (!dp.bLoadAllLoaded) {
                    returnValue = false
                    return false // stop iterating
                }
            }
        })

        // Check if any dimension has a filter all condition
        if (bScopeHasFilterAll)
            return true

        return returnValue;
    }

    getLocalRowScope(scope: IRowScope): IDataRow[] {
        const self = this
        let bFilterAll = false
        let dctFilterKeys: {[fieldName: string]: {[value: string | number]: string | number}} = {}

        _.forEach(this.dimensionProps, (dp, dimType) => {
            if(!dp.bHierarchyEnabled)return

            // If in scope, check coverage, else all must be loaded
            if (!(dimType in scope))
                return

            let sc = scope[dimType]
            if(sc.bAll)
                return

            if(_.isEmpty(sc.HierarchyNodes)){
                bFilterAll = true
                return false
            }

            let hier = sc.Hierarchy
            if (_.isEmpty(hier)) {
                // @ts-ignore
                hier = self.CubeSource.getDimensionHierarchy(dimType)
            }

            if (_.isEmpty(hier)){
                let msg = `getLocalRowScope: Unknown Hierarchy ${dimType}`
                console.log(msg)
                bFilterAll = true
                return false
            }

            dctFilterKeys[dp.KeyFieldName] = {}
            let dctKeys = dctFilterKeys[dp.KeyFieldName]
            // Find All Leaf Nodes and add to Filters
            let leafNodes1 = _.filter(sc.HierarchyNodes, (a) => (a?.NodeLevel == dp.LeafLevel))
            let leafNodes2 = hier.getChildren(sc.HierarchyNodes, dp.LeafLevel)
            _.forEach([... leafNodes1, ... leafNodes2], (aLeaf) => {
                dctKeys[aLeaf.NodeId] = aLeaf.NodeId
            })

        })

        if(bFilterAll)
            return []

        // Run through filters
        let returnValue = self.LoadedRows
        _.forEach(dctFilterKeys, (dctKeys, filterFieldName) => {
            returnValue = _.filter(returnValue, (aRow) => {
                return (aRow[filterFieldName] in dctKeys)
            })
        })

        return returnValue
    }

    async getRowScope(scope: IRowScope): Promise<IDataRow[]> {
        const self = this

        if(self.checkLoadScopeSync(scope))
            return self.getLocalRowScope(scope)

        // Queue Load
        self.queueRowScope(scope)

        let rv = self.waitForDataGroup.waitFor(scope, (chkState: IContinueConditionState<DataSourceJSData>) => {
            let checkScopeValue = self.checkLoadScopeSync(scope)
            return checkScopeValue
        }).then((ignore) => {
            let returnRows = self.getLocalRowScope(scope)
            return returnRows
        })
        self.DataSourceJSData_processQueue()
        return await rv
    }


    private queueRowScope(scope: IRowScope): void {
        const self = this

        _.forEach(this.dimensionProps, (dp, dimType) => {
            if(!dp.bHierarchyEnabled)return

            // If in scope, check coverage, else all must be loaded
            if(dimType in scope){

                // If the Dimension is fully loaded, then return true
                if(dp.bLoadAllLoaded)
                    return

                let rq = scope[dimType]
                if (rq.bAll || !dp.bLazyLoadingEnabled) {
                    dp.bLoadAllQueued = true
                    return
                }

                // Lazy Loading
                let hier = rq.Hierarchy
                if (_.isEmpty(hier)) {
                    // @ts-ignore
                    hier = self.CubeSource.getDimensionHierarchy(dimType)
                }

                if (_.isEmpty(hier)){
                    let msg = `queueRowScope: Unknown Hierarchy ${dimType}`
                    console.log(msg)
                    return
                }

                // Find All Leaf Nodes and add to Queue
                let leafNodes1 = _.filter(rq.HierarchyNodes, (a) => (a?.NodeLevel == dp.LeafLevel))
                let leafNodes2 = hier.getChildren(rq.HierarchyNodes, dp.LeafLevel)
                _.forEach([... leafNodes1, ... leafNodes2], (aLeaf) => {
                    if(dp.bUseKnownKeys){
                        if(!(aLeaf.NodeId in dp.KnownLeafKeysIdx))
                            return //-- Check next Key, not Known Key
                    }

                    if(!(aLeaf.NodeId in dp.dctLeafIdLoadedById)){
                        //-- Queue missing key
                        dp.dctLeafIdQueuedById[aLeaf.NodeId] = aLeaf.NodeId
                    }
                })

            } else {  //-- Dimension not in Scope, Assumes All scope required
                if(dp.bLoadAllLoaded)
                    return

                //-- All must be loaded
                dp.bLoadAllQueued = true
            }
        })

        self.qsProcessLoadQueue.Run({})

    }

    purgeScope(scope: IRowScope, options?: {bSkipPurgeContext?: boolean}): void {

        if(_.isEmpty(scope))
            return

        const self = this
        let bPurgeAll = false
        let bPurgeNone = false
        let dctFilterKeys: {[fieldName: string]: {[value: string | number]: string | number}} = {}

        _.forEach(this.dimensionProps, (dp, dimType) => {
            if(!dp.bHierarchyEnabled)return

            // If in scope, check coverage, else all must be loaded
            if (!(dimType in scope))
                return

            let sc = scope[dimType]
            if(sc.bAll) {
                bPurgeAll = true
                return
            }

            if(_.isEmpty(sc.HierarchyNodes)){
                bPurgeNone = true
                return
            }

            let hier = sc.Hierarchy
            if (_.isEmpty(hier)) {
                // @ts-ignore
                hier = self.CubeSource.getDimensionHierarchy(dimType)
            }

            if(_.isEmpty(sc.HierarchyNodes)){
                bPurgeAll = true
                return false
            }

            if (_.isEmpty(hier)){
                let msg = `purgeScope: Unknown Hierarchy ${dimType}`
                console.log(msg)
                bPurgeAll = true
                return
            }

            dctFilterKeys[dp.KeyFieldName] = {}
            let dctKeys = dctFilterKeys[dp.KeyFieldName]
            // Find All Leaf Nodes and add to Purge
            let leafNodes1 = _.filter(sc.HierarchyNodes, (a) => (a?.NodeLevel == dp.LeafLevel))
            let leafNodes2 = hier.getChildren(sc.HierarchyNodes, dp.LeafLevel)
            _.forEach([... leafNodes1, ... leafNodes2], (aLeaf) => {
                dctKeys[aLeaf.NodeId] = aLeaf.NodeId
            })



        })

        let removedItems: any[] = []
        if(bPurgeAll){
            removedItems = _.values(self.dctLoadedRows)
            self.dctLoadedRows = {}

        } else {
            // Run through filters
            let rows = _.values(self.dctLoadedRows)
            _.forEach(dctFilterKeys, (dctKeys, filterFieldName) => {
                removedItems.push(_.remove(rows, (aRow: any) => {
                    return (aRow[filterFieldName] in dctKeys)
                }))
            })
            self.dctLoadedRows = _.keyBy(rows, (a: any) => a[self.pkFieldName])
        }

        if(!options?.bSkipPurgeContext){
            self.dbContext.purgeCache(self.recordMapper.name, removedItems)
        }

        if (bPurgeAll) {
            _.forEach(self.dimensionProps, (dimProp) => {
                // Remove Loaded Keys
                dimProp.dctLeafIdLoadedById = {};
                dimProp.bLoadAllLoaded = false;
            });
        } else {
            // Purge keys of subset purged
            _.forEach(self.dimensionProps, (dimProp) => {
                // Remove Loaded Keys
                dimProp.bLoadAllLoaded = false;
                // TODO:
            });

        }

        self.DataSourceJSData_processQueue()

    }

    get OnRowsChanged(): BehaviorSubject<IDataSourceOnRowsChangedEvent> { return this._OnRowsChanged }
    get OnLoadProcessingComplete(): BehaviorSubject<ICubeSourceDataSource> { return this._OnLoadProcessingComplete }


    //-- Load Process Loop
    pauseLoadProcessing(): void {
        this.bQueueLoadProcessPaused = true
    }

    resumeLoadProcessing(): void {
        this.bQueueLoadProcessPaused = false
        this.DataSourceJSData_processQueue()
    }

    private DataSourceJSData_processQueue(): void {
        this.qsProcessLoadQueue.Run({})
    }

    private _DataSourceJSData_processQueue(): Promise<void> {

        let self = this
        let bDebugQueueProcessing = true
        function logDebug(...args: any[]){
            if(bDebugQueueProcessing)
                console.log(...args)
        }
        // 0. If Loading paused, exit
        // 1. If Load Running, exit
        // 2. Copy the queue
        // 3. Check if the queue is empty
        // 4. Load Volume Metrics
        // 5. Delete loaded items from the queue
        // 6. Notify addition

        logDebug('DataSourceJSData_processQueue started')

        // 0. If Loading Paused, exit
        if (self.bQueueLoadProcessPaused) {
            logDebug('DataSourceJSData_processQueue ended: paused')
            return Promise.resolve()
        }

        // 1. If Load Running, this, shouldn't happen when using Serializer
        if (self.bQueueLoadProcessRunning) {
            logDebug('DataSourceJSData_processQueue ended: already running')
            return Promise.resolve()
        }

        self.bQueueLoadProcessRunning = true;

        let returnValue = (async ()=> {
            let evtLoadedRows: IDataRow[] = []
            // 2. Copy the queue
            let arrLazyLoadDims: string[] = []
            let dctDimQueue: {
                [dimType: string]: {
                    bLazyLoadingEnabled: boolean
                    dctLeafIdQueuedById: { [id: number | string]: number | string }
                    bLoadAllQueued: boolean,
                    bLoadAllLoaded: boolean
                }
            } = {}

            _.forEach(self.dimensionProps, (dp, dimType) => {
                if(!dp.bHierarchyEnabled)return

                let queueCopy = {
                    bLazyLoadingEnabled: dp.bLazyLoadingEnabled,
                    dctLeafIdQueuedById: <{ [id: number | string]: number | string }> {},
                    bLoadAllQueued: dp.bLoadAllQueued || dp.bLoadAllLoaded,
                    bLoadAllLoaded: dp.bLoadAllLoaded
                }
                if (queueCopy.bLazyLoadingEnabled) {
                    arrLazyLoadDims.push(dimType)
                    // Copy Queue
                    queueCopy.dctLeafIdQueuedById = _.clone(dp.dctLeafIdQueuedById)
                    // Remove already loaded Items
                    _.forEach(dp.dctLeafIdLoadedById, (keyValue: string, key: string) => {
                        if (keyValue in queueCopy.dctLeafIdQueuedById)
                            delete queueCopy.dctLeafIdQueuedById[keyValue]
                    })
                }
                dctDimQueue[dimType] = queueCopy
            })


            // 3. Check if the queue is empty, or all Loaded
            let bQueueEmpty = true
            let bAllLoaded = true
            _.forEach(dctDimQueue, (dq, dimType) => {
                if(!dq.bLoadAllLoaded)
                    bAllLoaded = false
                if(_.isEmpty(dq.dctLeafIdQueuedById) && dq.bLoadAllLoaded)
                    return
                bQueueEmpty = false
                return
            })

            if(bQueueEmpty){

                _.forEach(self.dimensionProps, (a) => {
                    if(a.bLazyLoadingEnabled)
                        a.dctLeafIdQueuedById = {}
                })
                logDebug('DataSourceJSData_processQueue ended: empty queue')
                self.bQueueLoadProcessRunning = false
                return
            }


            // If All Loaded, return...
            if (bAllLoaded) {
                logDebug('DataSourceJSData_processQueue ended: all data already loaded')
                self.bQueueLoadProcessRunning = false
                return
            }

            // Mark running
            try {
                self.bQueueLoadProcessRunning = true
                logDebug('DataSourceJSData_processQueue set Queue Running.')
                self._LoadProgressProvider.start("Loading DataSource")

                let recResolver = new ESRecordResolver(self.dbContext, this.recordResolverOptions || {})

                // If count of Lazy Load Dimensions => 1, Use Lazy Loading Algorithm
                if(arrLazyLoadDims.length > 0) {

                    // Create Query to pull Data
                    for(let dimTypeLz of arrLazyLoadDims) {


                        let dqLz = dctDimQueue[dimTypeLz]
                        let dpLz = self.dimensionProps[dimTypeLz]
                        // Add the Condition for the Queued Items

                        // If this dimension is loaded, skip this dimension
                        if(dqLz.bLoadAllLoaded)
                            continue

                        if(_.isEmpty(dqLz.dctLeafIdQueuedById) && !dqLz.bLoadAllQueued)
                            continue

                        let recCond = self.createMapperStandardCondition();
                        if(dqLz.bLoadAllQueued){
                            // Exclude the already Loaded
                            let idsToExclude = _.values(dpLz.dctLeafIdLoadedById)
                            if(!_.isEmpty(idsToExclude))
                                recCond[dpLz.KeyFieldName] = {notIn: idsToExclude}
                        } else {
                            // Include the Queued
                            let idsToInclude = _.values(dqLz.dctLeafIdQueuedById)
                            recCond[dpLz.KeyFieldName] = {inq: idsToInclude}
                        }
                        let bOtherDimensionEmpty = false
                        // For All Other Dimensions, add condition for "Loaded"
                        _.forEach(self.dimensionProps, (dpOther, dimTypeOther) => {
                            if(dimTypeOther == dimTypeLz)
                                return

                            if(!dpOther.bHierarchyEnabled)
                                return

                            if(!dpOther.bLazyLoadingEnabled)
                                return

                            if(dpOther.bLoadAllLoaded)
                                return

                            //-- Add Condition for the Loaded Items, using dpOther
                            let dqOther = dctDimQueue[dimTypeOther]
                            // If Condition would cause statement to return empty set, set flag and bail
                            if(_.isEmpty(dpOther.dctLeafIdLoadedById)){
                                bOtherDimensionEmpty = true
                                return false
                            }

                            let idsToInclude = _.values(dpOther.dctLeafIdLoadedById)
                            recCond[dpOther.KeyFieldName] = {inq: idsToInclude}

                        })

                        if(!bOtherDimensionEmpty) {
                            logDebug('Queue Condition: ' + JSON.stringify(recCond))
                            // Use Record Resolver to get Parents..
                            let results = await recResolver.queryAllWithRelationships(self.recordMapper.name, recCond)
                            if (!_.isArray(results)) {
                                logDebug('DataSourceJSData_processQueue set Queue Running = false, not array.')
                                self.bQueueLoadProcessRunning = false
                                self._LoadProgressProvider.finish()
                                return
                            }

                            results = _.filter(results, (a) => a.isActive)

                            _.forEach(results, (aRow: any) => {
                                let pk = aRow[self.pkFieldName]
                                self.dctLoadedRows[pk] = aRow
                                evtLoadedRows.push(aRow)
                            })
                            self.OnRowsChanged.next({
                                DataSource: self, Change: DataSourceRowsChange.Loaded, Rows: results
                            })

                        }

                        // 5. Delete loaded items from the queue, Update Loaded List
                        if(dqLz.bLoadAllQueued)
                            dpLz.bLoadAllLoaded = true
                        _.forEach(dqLz.dctLeafIdQueuedById, (value, key) => {
                            dpLz.dctLeafIdLoadedById[key] = value
                        })

                        _.forEach(self.dimensionProps, (dp) => {
                            if(!dp.bHierarchyEnabled)
                                return

                            if(dp.bLazyLoadingEnabled)
                                return
                            dp.bLoadAllLoaded = true
                            dp.bLoadAllQueued = false
                            dp.dctLeafIdQueuedById = {}
                        })
                    }

                } else {
                    //-- Not Lazy Loading, load Everything in one shot
                    // Use Record Resolver to get Parents..
                    let recCond = self.createMapperStandardCondition()
                    let results = await recResolver.queryAllWithRelationships(self.recordMapper.name, recCond)
                    results = _.filter(results, (a) => a.isActive)
                    // It's possible to have rows that have already been added by the sync process, so merge
                    _.defaults(self.dctLoadedRows, _.keyBy(results, (a: any) => a[self.pkFieldName]))

                    _.forEach(self.dimensionProps, (dp) => {
                        if(!dp.bHierarchyEnabled)
                            return

                        dp.bLoadAllLoaded = true
                        dp.bLoadAllQueued = false
                        dp.dctLeafIdQueuedById = {}
                    })
                    self.OnRowsChanged.next({
                        DataSource: self, Change: DataSourceRowsChange.Loaded, Rows: results
                    })
                }

                logDebug('DataSourceJSData_processQueue set Queue Running = false, done.')
                self.bQueueLoadProcessRunning = false

                self._LoadProgressProvider.finish()

                self.OnLoadProcessingComplete.next(self)

                self.OnRowsChanged.next({DataSource: self, Change: DataSourceRowsChange.Loaded, Rows: evtLoadedRows })

                self.waitForDataGroup.checkContinueConditions()


            } catch (err) {
                debugger
                logDebug('DataSourceJSData_processQueue set Queue Running = false, error: ', err)
                self.bQueueLoadProcessRunning = false
                self._LoadProgressProvider.exception(err)
            }
        })();

        return returnValue
    }




    private createMapperStandardCondition(): {[id:string]: any} {
        let csDSRow = this._CubeSourceDataSourceRow
        let csRow = csDSRow.WB_CubeSourceRow

        let cnd: any = JSONUtils.parse(csDSRow.cif.FILTER_CONDITION, {})

        if(!_.isEmpty(_.trim(csDSRow.cif.PARAMETER1_FIELDNAME))){
            let filterKey = csDSRow.cif.PARAMETER1_FIELDNAME
            let paramName = csRow.cif.PARAMETER1_NAME

            let paramValue = this._CubeSource.getParameter(paramName) || []
            cnd[filterKey] = {inq: paramValue}
        }

        if(!_.isEmpty(_.trim(csDSRow.cif.PARAMETER2_FIELDNAME))){
            let filterKey = csDSRow.cif.PARAMETER2_FIELDNAME
            let paramName = csRow.cif.PARAMETER2_NAME

            let paramValue = this._CubeSource.getParameter(paramName) || []
            cnd[filterKey] = {inq: paramValue}
        }

        return cnd
    }
}